// PWA — 最近生成列表的 IndexedDB 持久层（Req 8.5 / 任务 9.7）
//
// 本地维护最近 20 条生成记录（jobId、提示词、参数、可选缩略图引用、createdAt），
// 以便离线回看提示词与参数。容量与排序逻辑复用 @comfy/shared 的 insertRecent
// （2.13，按 createdAt 倒序裁剪至 MAX_RECENT=20），本模块只负责读写 IndexedDB。
//
// 设计要点：
// - 使用极小的 Promise 包装直接操作 IndexedDB，不引入运行时依赖。
// - IDBFactory 可注入（默认取全局 indexedDB），方便用 fake-indexeddb 做单元测试。
// - 单一对象仓库 "recent"，主键为 jobId；同 jobId 写入按更新处理（put）。

import { insertRecent } from "@comfy/shared";
import type { RecentGeneration } from "@comfy/shared";

const DB_NAME = "comfy-recent";
const DB_VERSION = 1;
const STORE = "recent";

/** 允许测试注入 fake-indexeddb；运行时默认使用浏览器全局 indexedDB。 */
export interface RecentStoreOptions {
  indexedDB?: IDBFactory;
}

function resolveFactory(opts?: RecentStoreOptions): IDBFactory {
  const factory = opts?.indexedDB ?? globalThis.indexedDB;
  if (!factory) {
    throw new Error("IndexedDB 不可用：当前环境未提供 indexedDB。");
  }
  return factory;
}

/** 打开（或在首次/升级时初始化）数据库。 */
function openDb(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = factory.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "jobId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("打开 IndexedDB 失败"));
  });
}

/** 将一个 IDBRequest 包装为 Promise。 */
function promisifyRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB 请求失败"));
  });
}

/** 等待一个事务提交完成。 */
function awaitTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB 事务被中止"));
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB 事务出错"));
  });
}

async function withDb<T>(
  opts: RecentStoreOptions | undefined,
  fn: (db: IDBDatabase) => Promise<T>,
): Promise<T> {
  const db = await openDb(resolveFactory(opts));
  try {
    return await fn(db);
  } finally {
    db.close();
  }
}

/**
 * 读取全部最近生成记录，按 createdAt 倒序（最新在前）返回。
 */
export async function getRecent(
  opts?: RecentStoreOptions,
): Promise<RecentGeneration[]> {
  return withDb(opts, async (db) => {
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const all = await promisifyRequest(
      store.getAll() as IDBRequest<RecentGeneration[]>,
    );
    return all.slice().sort((a, b) => b.createdAt - a.createdAt);
  });
}

/**
 * 新增一条最近生成记录。
 *
 * 读取现有列表后复用 shared 的 insertRecent 进行「倒序 + 裁剪至 20 条」，
 * 再将结果整体持久化（写入保留集合、删除被裁掉的记录），保证存储中始终
 * 只保留最新的 MAX_RECENT 条。返回写入后的列表（倒序）。
 */
export async function addRecent(
  entry: RecentGeneration,
  opts?: RecentStoreOptions,
): Promise<RecentGeneration[]> {
  return withDb(opts, async (db) => {
    const readTx = db.transaction(STORE, "readonly");
    const existing = await promisifyRequest(
      readTx.objectStore(STORE).getAll() as IDBRequest<RecentGeneration[]>,
    );

    // jobId 是仓库主键（唯一）。先剔除同 jobId 的旧记录，避免重复，
    // 再交由 shared 的 insertRecent 完成倒序与裁剪。
    const deduped = existing.filter((r) => r.jobId !== entry.jobId);
    const next = insertRecent(deduped, entry);
    const keep = new Set(next.map((r) => r.jobId));

    const writeTx = db.transaction(STORE, "readwrite");
    const store = writeTx.objectStore(STORE);
    // 删除不在保留集合中的旧记录（被裁剪掉的）。
    for (const r of existing) {
      if (!keep.has(r.jobId)) {
        store.delete(r.jobId);
      }
    }
    // 写入/更新保留的记录。
    for (const r of next) {
      store.put(r);
    }
    await awaitTx(writeTx);
    return next;
  });
}

/** 清空全部最近生成记录。 */
export async function clearRecent(opts?: RecentStoreOptions): Promise<void> {
  return withDb(opts, async (db) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    await awaitTx(tx);
  });
}
